"use client";

import { content } from "@/config/content";
import ImagePlaceholder from "@/components/shared/ImagePlaceholder";
import { useSiteSettings } from "@/lib/useSiteSettings";
import FavoriteButton from "@/components/public/FavoriteButton";

export default function ArtworkCard({
  artwork,
  onQuickView,
  priority = false,
}) {
  const isAvailable = artwork.status === "available";
  const { settings } = useSiteSettings("pricing");
  // Phase 3.7 — artwork button text from CMS
  const { settings: art } = useSiteSettings("artwork");
  const labelClaim = art.artwork_claim_button || "我要認領";
  const labelDetail = art.artwork_detail_button || "查看詳情";
  const fileDownload = Number(settings.price_file_download || 2500);
  const displayPrice =
    Number(artwork.price) > 0 ? Number(artwork.price) : fileDownload;

  const handleClick = () => {
    if (onQuickView) {
      onQuickView(artwork);
    }
  };

  return (
    <div
      data-artwork-id={artwork.id}
      role="button"
      tabIndex={0}
      className="group block cursor-pointer overflow-hidden transition-transform duration-150 active:scale-[0.97]"
      style={{
        backgroundColor: "var(--color-cardBg)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--color-cardBorder)",
        boxShadow: "var(--shadow-card)",
        WebkitTapHighlightColor: "transparent",
      }}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleClick();
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "var(--shadow-card-hover)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "var(--shadow-card)";
      }}
    >
      <div
        className="relative overflow-hidden"
        style={{ opacity: isAvailable ? 1 : 0.65 }}
      >
        {/* 圖片 — 桌面 hover 微放大 */}
        <div className="transition-transform duration-300 md:group-hover:scale-105">
          <ImagePlaceholder
            name={artwork.name}
            image={artwork.image}
            artworkId={artwork.id}
            aspectRatio="1/1"
            className="w-full"
            priority={priority}
          />
        </div>

        <FavoriteButton artworkId={artwork.id} />

        {/* 已帶走對角章印 — flash sheet tone，置中 -12° 旋轉 */}
        {!isAvailable && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            aria-hidden="true"
          >
            <div
              className="select-none"
              style={{
                transform: "rotate(-12deg)",
                padding: "6px 18px",
                border: "2.5px solid rgba(185,28,28,0.88)",
                borderRadius: "4px",
                color: "rgba(185,28,28,0.95)",
                fontSize: "14px",
                fontWeight: 700,
                letterSpacing: "0.15em",
                fontFamily: "var(--font-heading)",
                backgroundColor: "rgba(255,255,255,0.78)",
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
                boxShadow:
                  "0 2px 10px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(255,255,255,0.4)",
              }}
            >
              已帶走 ♡
            </div>
          </div>
        )}

        {isAvailable && (
          <span
            className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 md:px-2.5 text-xs font-medium"
            style={{
              backgroundColor: "var(--color-available)",
              color: "var(--color-availableText, #fff)",
              borderRadius: "var(--radius-full)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: "var(--color-availableText, #fff)" }}
            />
            {art.artwork_available_badge || content.gallery.availableLabel}
          </span>
        )}

        {/* 桌面：hover 遮罩 */}
        <div
          className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <span
            className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-white tracking-wider"
            style={{
              border: "1.5px solid rgba(255,255,255,0.7)",
              borderRadius: "var(--radius-full)",
              backdropFilter: "blur(4px)",
              fontFamily: "var(--font-body)",
            }}
          >
            {isAvailable ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {labelClaim}
              </>
            ) : (
              labelDetail
            )}
          </span>
        </div>
      </div>

      <div className="p-2.5 md:p-3 lg:p-4">
        <h3
          className="text-xs md:text-sm font-medium mb-1 truncate"
          style={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-text)",
          }}
        >
          {artwork.name}
        </h3>
        <div className="flex items-center justify-between">
          <p
            className="text-xs md:text-sm font-medium"
            style={{
              color: isAvailable
                ? "var(--color-accent)"
                : "var(--color-textMuted)",
            }}
          >
            {content.gallery.pricePrefix}
            {displayPrice.toLocaleString()}
          </p>
          <span
            className="text-xs md:text-xs font-medium flex items-center gap-0.5 transition-transform duration-200 group-hover:translate-x-0.5"
            style={{
              color: "var(--color-accent)",
              fontFamily: "var(--font-body)",
            }}
          >
            {isAvailable ? labelClaim : labelDetail}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}
